import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import moment from 'moment'
import 'moment/locale/es'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import { format, startOfMonth, endOfMonth, addMonths, parseISO } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Label } from '../components/ui/label'
import { Input } from '../components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'
import {
  CalendarDays, ListTodo, Bell, Plus, ChevronLeft, ChevronRight,
  ArrowLeft, Calendar as CalendarIcon, Clock, CheckCircle2, 
  AlertTriangle, Loader2, GripVertical, RefreshCw
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Detect touch device
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

// Setup localizer
const localizer = momentLocalizer(moment)

// Drag and drop calendar (only for desktop)
const DnDCalendar = withDragAndDrop(Calendar)

export default function CalendarPage() {
  const { t, locale } = useLanguage()
  const navigate = useNavigate()
  const dateLocale = locale === 'es-MX' ? es : enUS
  const isTouch = useMemo(() => isTouchDevice(), [])
  
  // Set moment locale
  useEffect(() => {
    moment.locale(locale === 'es-MX' ? 'es' : 'en')
  }, [locale])
  
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState('month')
  
  // Modal states
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null)
  
  // Reschedule form
  const [rescheduleData, setRescheduleData] = useState({
    new_date: '',
    new_time: ''
  })
  const [rescheduling, setRescheduling] = useState(false)

  // Fetch calendar events
  const fetchEvents = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true)
    try {
      const start = format(startOfMonth(addMonths(currentDate, -1)), 'yyyy-MM-dd')
      const end = format(endOfMonth(addMonths(currentDate, 1)), 'yyyy-MM-dd')
      
      const response = await axios.get(`${API}/calendar/events`, {
        params: { start_date: start, end_date: end }
      })
      
      // Transform events for react-big-calendar
      const calendarEvents = response.data.events.map(event => {
        const startDate = parseISO(event.start_at)
        const endDate = event.end_at ? parseISO(event.end_at) : startDate
        
        return {
          id: event.id,
          title: event.title,
          start: startDate,
          end: endDate,
          allDay: event.all_day,
          resource: {
            source_type: event.source_type,
            source_id: event.source_id,
            status: event.status,
            color: event.color,
            urgency: event.urgency,
            category: event.category,
            amount: event.amount
          }
        }
      })
      
      setEvents(calendarEvents)
    } catch (error) {
      console.error('Error fetching calendar events:', error)
      toast.error(t('common.error'))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [currentDate, t])

  // Manual refresh handler
  const handleRefresh = () => {
    fetchEvents(true)
  }

  useEffect(() => {
    fetchEvents()
    
    // Auto-refresh when user comes back to the page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchEvents(false) // Silent refresh
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchEvents])

  // Handle event click
  const handleEventClick = (event) => {
    setSelectedEvent(event)
    setShowEventModal(true)
  }

  // Navigate to original item
  const goToOriginal = () => {
    if (!selectedEvent) return
    const { source_type, source_id } = selectedEvent.resource
    setShowEventModal(false)
    
    if (source_type === 'task') {
      navigate('/tareas', { state: { highlightTask: source_id } })
    } else {
      navigate('/recordatorios', { state: { highlightReminder: source_id } })
    }
  }

  // Open reschedule modal
  const openReschedule = () => {
    if (!selectedEvent) return
    
    // Check if completed
    const status = selectedEvent.resource.status
    if (status === 'completed' || status === 'done' || status === 'completada' || status === 'Terminado') {
      toast.error(t('calendar.cannotRescheduleCompleted'))
      return
    }
    
    setRescheduleData({
      new_date: format(selectedEvent.start, 'yyyy-MM-dd'),
      new_time: selectedEvent.allDay ? '' : format(selectedEvent.start, 'HH:mm')
    })
    setShowEventModal(false)
    setShowRescheduleModal(true)
  }

  // Handle reschedule submit
  const handleReschedule = async () => {
    if (!selectedEvent || !rescheduleData.new_date) return
    
    setRescheduling(true)
    try {
      const { source_type, source_id } = selectedEvent.resource
      const endpoint = source_type === 'task' 
        ? `${API}/calendar/events/task/${source_id}/reschedule`
        : `${API}/calendar/events/reminder/${source_id}/reschedule`
      
      await axios.put(endpoint, {
        new_date: rescheduleData.new_date,
        new_time: rescheduleData.new_time || null
      })
      
      toast.success(t('calendar.rescheduled'))
      setShowRescheduleModal(false)
      setSelectedEvent(null)
      fetchEvents()
    } catch (error) {
      console.error('Error rescheduling:', error)
      toast.error(error.response?.data?.detail || t('common.error'))
    } finally {
      setRescheduling(false)
    }
  }

  // Handle drag and drop (desktop only)
  const handleEventDrop = async ({ event, start, end, isAllDay }) => {
    // Check if completed
    const status = event.resource.status
    if (status === 'completed' || status === 'done' || status === 'completada' || status === 'Terminado') {
      toast.error(t('calendar.cannotRescheduleCompleted'))
      fetchEvents() // Refresh to revert
      return
    }
    
    const { source_type, source_id } = event.resource
    const newDate = format(start, 'yyyy-MM-dd')
    const newTime = isAllDay || event.allDay ? null : format(start, 'HH:mm')
    
    try {
      const endpoint = source_type === 'task' 
        ? `${API}/calendar/events/task/${source_id}/reschedule`
        : `${API}/calendar/events/reminder/${source_id}/reschedule`
      
      await axios.put(endpoint, {
        new_date: newDate,
        new_time: newTime
      })
      
      toast.success(t('calendar.rescheduled'))
      fetchEvents()
    } catch (error) {
      console.error('Error rescheduling:', error)
      toast.error(error.response?.data?.detail || t('common.error'))
      fetchEvents() // Refresh to revert
    }
  }

  // Handle slot selection (for quick create)
  const handleSelectSlot = ({ start, end }) => {
    setSelectedSlot({ start, end })
    setShowCreateModal(true)
  }

  // Create new task/reminder from slot
  const createFromSlot = (type) => {
    setShowCreateModal(false)
    if (type === 'task') {
      const dateStr = format(selectedSlot.start, 'yyyy-MM-dd')
      navigate('/tareas', { state: { prefillDate: dateStr } })
    } else {
      const dateStr = format(selectedSlot.start, 'yyyy-MM-dd')
      navigate('/recordatorios', { state: { prefillDate: dateStr } })
    }
  }

  // Custom event styling
  const eventStyleGetter = (event) => {
    const color = event.resource.color || '#3b82f6'
    const isCompleted = event.resource.status === 'completed' || 
                        event.resource.status === 'done' ||
                        event.resource.status === 'completada' ||
                        event.resource.status === 'Terminado'
    
    return {
      style: {
        backgroundColor: color,
        borderRadius: '4px',
        opacity: isCompleted ? 0.6 : 1,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '12px',
        padding: '2px 4px',
        cursor: isTouch ? 'pointer' : 'grab'
      }
    }
  }

  // Custom event component
  const EventComponent = ({ event }) => {
    const { source_type, status } = event.resource
    const isCompleted = status === 'completed' || status === 'done' || status === 'completada' || status === 'Terminado'
    const Icon = source_type === 'task' ? ListTodo : Bell
    
    return (
      <div className="flex items-center gap-1 truncate" data-testid={`calendar-event-${event.id}`}>
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span className={cn("truncate", isCompleted && "line-through")}>
          {event.title}
        </span>
      </div>
    )
  }

  // Navigation handlers
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate)
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  const goPrevious = () => {
    setCurrentDate(prev => {
      if (view === 'month') {
        return addMonths(prev, -1)
      }
      return new Date(prev.getTime() - 7 * 24 * 60 * 60 * 1000)
    })
  }

  const goNext = () => {
    setCurrentDate(prev => {
      if (view === 'month') {
        return addMonths(prev, 1)
      }
      return new Date(prev.getTime() + 7 * 24 * 60 * 60 * 1000)
    })
  }

  // Custom messages for react-big-calendar
  const messages = useMemo(() => ({
    today: t('common.today'),
    previous: t('common.previous'),
    next: t('common.next'),
    month: t('calendar.month'),
    week: t('calendar.week'),
    day: t('calendar.day'),
    agenda: t('calendar.agenda'),
    date: t('finance.date'),
    time: t('tasks.dueTime'),
    event: t('calendar.event'),
    noEventsInRange: t('calendar.noEvents'),
    allDay: t('calendar.allDay'),
    showMore: (total) => `+${total} ${t('calendar.more')}`
  }), [t])

  // Calendar props
  const calendarProps = {
    localizer,
    events,
    startAccessor: "start",
    endAccessor: "end",
    style: { height: 'calc(100vh - 200px)', minHeight: '500px' },
    views: ['month', 'week'],
    view,
    onView: setView,
    date: currentDate,
    onNavigate: handleNavigate,
    onSelectEvent: handleEventClick,
    onSelectSlot: handleSelectSlot,
    selectable: true,
    eventPropGetter: eventStyleGetter,
    components: {
      event: EventComponent
    },
    messages,
    popup: true,
    toolbar: false // We use custom toolbar
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="calendar-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="calendar-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary" />
                  {t('calendar.title')}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {t('calendar.subtitle')}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              className="rounded-full"
              data-testid="refresh-calendar-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Custom Toolbar */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goPrevious}
                data-testid="calendar-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToToday}
                data-testid="calendar-today-btn"
              >
                {t('common.today')}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goNext}
                data-testid="calendar-next-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <h2 className="text-lg font-semibold capitalize">
              {format(currentDate, view === 'month' ? 'MMMM yyyy' : "'Semana' w, MMMM yyyy", { locale: dateLocale })}
            </h2>
            
            <div className="flex items-center gap-2">
              <Button
                variant={view === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('month')}
                data-testid="calendar-month-view-btn"
              >
                {t('calendar.month')}
              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('week')}
                data-testid="calendar-week-view-btn"
              >
                {t('calendar.week')}
              </Button>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span>{t('calendar.tasks')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-orange-500"></div>
              <span>{t('calendar.reminders')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-400"></div>
              <span>{t('calendar.completed')}</span>
            </div>
            {!isTouch && (
              <div className="flex items-center gap-1 ml-auto">
                <GripVertical className="w-3 h-3" />
                <span>{t('calendar.dragToReschedule')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="px-4 py-4">
        <Card>
          <CardContent className="p-2 sm:p-4">
            {isTouch ? (
              <Calendar {...calendarProps} />
            ) : (
              <DnDCalendar 
                {...calendarProps}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventDrop}
                resizable={false}
                draggableAccessor={() => true}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* FAB for quick create */}
      <Button
        className="fixed bottom-24 right-20 h-14 w-14 rounded-full shadow-lg z-20"
        onClick={() => setShowCreateModal(true)}
        data-testid="calendar-add-btn"
      >
        <Plus className="w-6 h-6" />
      </Button>

      {/* Event Detail Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.resource.source_type === 'task' ? (
                <ListTodo className="w-5 h-5 text-blue-500" />
              ) : (
                <Bell className="w-5 h-5 text-orange-500" />
              )}
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span>
                  {format(selectedEvent.start, 'EEEE, d MMMM yyyy', { locale: dateLocale })}
                </span>
              </div>
              
              {!selectedEvent.allDay && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{format(selectedEvent.start, 'HH:mm')}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Badge 
                  variant="outline"
                  className={cn(
                    selectedEvent.resource.source_type === 'task' 
                      ? 'border-blue-500 text-blue-500' 
                      : 'border-orange-500 text-orange-500'
                  )}
                >
                  {selectedEvent.resource.source_type === 'task' 
                    ? t('calendar.task') 
                    : t('calendar.reminder')}
                </Badge>
                
                {(selectedEvent.resource.status === 'completed' || 
                  selectedEvent.resource.status === 'done' ||
                  selectedEvent.resource.status === 'completada' ||
                  selectedEvent.resource.status === 'Terminado') && (
                  <Badge variant="outline" className="border-green-500 text-green-500">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {t('calendar.completed')}
                  </Badge>
                )}
              </div>
              
              {selectedEvent.resource.amount && (
                <div className="text-lg font-semibold">
                  ${selectedEvent.resource.amount.toLocaleString()}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={goToOriginal}
              className="w-full sm:w-auto"
              data-testid="calendar-view-original-btn"
            >
              {t('calendar.viewOriginal')}
            </Button>
            
            {selectedEvent && 
             selectedEvent.resource.status !== 'completed' && 
             selectedEvent.resource.status !== 'done' &&
             selectedEvent.resource.status !== 'completada' &&
             selectedEvent.resource.status !== 'Terminado' && (
              <Button 
                onClick={openReschedule}
                className="w-full sm:w-auto"
                data-testid="calendar-reschedule-btn"
              >
                {t('calendar.reschedule')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('calendar.reschedule')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('calendar.newDate')}</Label>
              <Input
                type="date"
                value={rescheduleData.new_date}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, new_date: e.target.value }))}
                data-testid="reschedule-date-input"
              />
            </div>
            
            <div>
              <Label>{t('calendar.newTime')} ({t('settings.optional')})</Label>
              <Input
                type="time"
                value={rescheduleData.new_time}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, new_time: e.target.value }))}
                data-testid="reschedule-time-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('calendar.leaveEmptyForAllDay')}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRescheduleModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleReschedule}
              disabled={rescheduling || !rescheduleData.new_date}
              data-testid="reschedule-confirm-btn"
            >
              {rescheduling ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('calendar.quickCreate')}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="h-24 flex-col gap-2"
              onClick={() => createFromSlot('task')}
              data-testid="create-task-btn"
            >
              <ListTodo className="w-8 h-8 text-blue-500" />
              <span>{t('calendar.newTask')}</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-24 flex-col gap-2"
              onClick={() => createFromSlot('reminder')}
              data-testid="create-reminder-btn"
            >
              <Bell className="w-8 h-8 text-orange-500" />
              <span>{t('calendar.newReminder')}</span>
            </Button>
          </div>
          
          {selectedSlot && (
            <p className="text-center text-sm text-muted-foreground">
              {format(selectedSlot.start, 'EEEE, d MMMM', { locale: dateLocale })}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
